export interface PickListItem {
  name: string;
  category: string;
  target: number;
  actual: number;
  needed: number;
}

interface PickListProps {
  stationName: string;
  stationCode?: string;
  orderId?: number;
  items: PickListItem[];
  submittedAt?: string;
  printedAt?: string;
}

const CATEGORY_ORDER = ['Airway', 'Breathing', 'Circulation', 'Medications', 'Splinting', 'Burn', 'OB/Peds', 'Misc'];

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PickList({ stationName, stationCode, orderId, items, submittedAt, printedAt }: PickListProps) {
  const grouped: Record<string, PickListItem[]> = {};
  for (const item of items) {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  }
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  const totalUnits = items.reduce((sum, i) => sum + i.needed, 0);
  const today = printedAt ?? new Date().toISOString();

  return (
    <div className="picklist mx-auto max-w-3xl bg-white text-black font-sans">
      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="flex items-start justify-between border-b-2 border-black pb-3 mb-4">
        <div className="flex items-center gap-3">
          <img src="/dcvfd-badge.svg" alt="DCVFD" className="h-14 w-auto" />
          <div>
            <h1 className="text-xl font-bold leading-tight">Resupply Pick List</h1>
            <p className="text-xs text-gray-600 leading-tight">Dale City Volunteer Fire Department</p>
            <p className="text-xs text-gray-600 leading-tight">EMS Supply Logistics</p>
          </div>
        </div>
        <div className="text-right text-xs">
          {orderId !== undefined && (
            <p className="font-mono text-sm font-bold">ORDER #{String(orderId).padStart(5, '0')}</p>
          )}
          <p className="text-gray-600 mt-0.5">Printed {formatDate(today)}</p>
        </div>
      </header>

      {/* ── Station / Summary panel ──────────────────────────────── */}
      <section className="grid grid-cols-3 gap-2 mb-4 border border-gray-300 rounded">
        <div className="px-3 py-2 border-r border-gray-300">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Deliver To</p>
          <p className="text-sm font-bold">{stationName}</p>
          {stationCode && <p className="text-xs text-gray-600">{stationCode}</p>}
        </div>
        <div className="px-3 py-2 border-r border-gray-300">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Submitted</p>
          <p className="text-sm font-bold">{submittedAt ? formatDate(submittedAt) : '—'}</p>
        </div>
        <div className="px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Totals</p>
          <p className="text-sm font-bold">
            {items.length} line{items.length !== 1 ? 's' : ''} · {totalUnits} unit{totalUnits !== 1 ? 's' : ''}
          </p>
        </div>
      </section>

      {/* ── Empty state ──────────────────────────────────────────── */}
      {items.length === 0 && (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded">
          <p className="text-sm text-gray-600">No items short — nothing to pick.</p>
        </div>
      )}

      {/* ── Items by category ────────────────────────────────────── */}
      {orderedCategories.map((category) => {
        const catItems = grouped[category];
        const catUnits = catItems.reduce((s, i) => s + i.needed, 0);
        return (
          <section key={category} className="mb-4 break-inside-avoid">
            <div className="flex items-baseline justify-between border-b border-black mb-1 pb-0.5">
              <h2 className="text-sm font-bold uppercase tracking-wide">{category}</h2>
              <span className="text-[10px] text-gray-600 font-medium">
                {catItems.length} line{catItems.length !== 1 ? 's' : ''} · {catUnits} unit{catUnits !== 1 ? 's' : ''}
              </span>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="py-1 font-medium w-8">✓</th>
                  <th className="py-1 font-medium">Item</th>
                  <th className="py-1 font-medium text-right w-16">PAR</th>
                  <th className="py-1 font-medium text-right w-16">On Hand</th>
                  <th className="py-1 font-medium text-right w-16">Pull</th>
                </tr>
              </thead>
              <tbody>
                {catItems.map((item) => (
                  <tr key={item.name} className="border-b border-gray-200">
                    <td className="py-1.5 align-middle">
                      <span className="inline-block h-4 w-4 border-2 border-black rounded-sm" />
                    </td>
                    <td className="py-1.5 pr-2">{item.name}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums">{item.target}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums">{item.actual}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums font-bold">{item.needed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}

      {/* ── Signature footer ─────────────────────────────────────── */}
      <footer className="mt-6 pt-4 border-t-2 border-black break-inside-avoid">
        <div className="grid grid-cols-2 gap-6 text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-3">Picked By</p>
            <div className="border-b border-black h-6 mb-1"></div>
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>Name / Signature</span>
              <span>Date</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-3">
              Verified / Delivered By
            </p>
            <div className="border-b border-black h-6 mb-1"></div>
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>Name / Signature</span>
              <span>Date</span>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-1">Notes</p>
          <div className="border border-gray-300 rounded h-12"></div>
        </div>
      </footer>
    </div>
  );
}
